import { useEffect } from 'react';

interface SchemaOrgProps {
  type?: 'home' | 'transport' | 'demenagement' | 'coursier' | 'livraison' | 'blog' | 'LocalBusiness' | 'Service';
  faqItems?: Array<{ question: string; answer: string }>;
  name?: string;
  description?: string;
  areaServed?: string;
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://transporteurexpressidf.fr';

export default function SchemaOrg({ type = 'home', faqItems, name, description, areaServed }: SchemaOrgProps) {
  useEffect(() => {
    // Remove existing schema script
    const existingScript = document.getElementById('schema-org-jsonld');
    if (existingScript) {
      existingScript.remove();
    }

    let schemaData: any = {};

    // Base LocalBusiness schema
    const baseBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: name || 'Transporteur Express IDF',
      image: `${SITE_URL}/logo.png`,
      url: SITE_URL,
      telephone: '+33632410246',
      priceRange: '€€',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Paris',
        addressLocality: 'Paris',
        postalCode: '75000',
        addressRegion: 'Île-de-France',
        addressCountry: 'FR'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '48.8566',
        longitude: '2.3522'
      },
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        opens: '00:00',
        closes: '23:59'
      },
      areaServed: areaServed || 'Paris, Île-de-France',
      description: description || 'Service de transport express, coursier et livraison urgente à Paris et en Île-de-France. Disponible 24h/24 et 7j/7.'
    };

    switch (type) {
      case 'home':
        schemaData = {
          '@context': 'https://schema.org',
          '@graph': [
            baseBusinessSchema,
            {
              '@type': 'WebSite',
              '@id': `${SITE_URL}/#website`,
              url: SITE_URL,
              name: 'Transporteur Express IDF',
              description: 'Service de transport express et coursier à Paris',
              publisher: {
                '@id': `${SITE_URL}/#organization`
              }
            },
            {
              '@type': 'Organization',
              '@id': `${SITE_URL}/#organization`,
              name: 'Transporteur Express IDF',
              url: SITE_URL,
              logo: {
                '@type': 'ImageObject',
                url: `${SITE_URL}/logo.png`
              },
              contactPoint: {
                '@type': 'ContactPoint',
                telephone: '+33632410246',
                contactType: 'customer service',
                areaServed: 'FR',
                availableLanguage: 'French'
              }
            }
          ]
        };
        break;

      case 'transport':
        schemaData = {
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: 'Transport de Marchandises',
          provider: baseBusinessSchema,
          areaServed: 'Paris, Île-de-France',
          description: 'Service de transport de marchandises professionnel à Paris et en Île-de-France'
        };
        break;

      case 'demenagement':
        schemaData = {
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: 'Déménagement',
          provider: baseBusinessSchema,
          areaServed: 'Paris, Île-de-France',
          description: 'Service de déménagement professionnel à Paris et en Île-de-France'
        };
        break;

      case 'coursier':
        schemaData = {
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: 'Coursier Express',
          provider: baseBusinessSchema,
          areaServed: 'Paris, Île-de-France',
          description: 'Service de coursier express à Paris et en Île-de-France'
        };
        break;

      case 'livraison':
        schemaData = {
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: 'Livraison Express',
          provider: baseBusinessSchema,
          areaServed: 'Paris, Île-de-France',
          description: 'Service de livraison express à Paris et en Île-de-France'
        };
        break;

      case 'blog':
        schemaData = {
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'Blog Transport Paris',
          description: 'Conseils et guides sur le transport, la livraison et le déménagement à Paris',
          url: `${SITE_URL}/blog`,
          publisher: baseBusinessSchema
        };
        break;

      case 'LocalBusiness':
        schemaData = {
          ...baseBusinessSchema,
          name: name || baseBusinessSchema.name,
          description: description || baseBusinessSchema.description,
          areaServed: areaServed || baseBusinessSchema.areaServed
        };
        break;

      case 'Service':
        schemaData = {
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: name || 'Transport Express',
          provider: baseBusinessSchema,
          areaServed: areaServed || 'Paris, Île-de-France',
          description: description || 'Service de transport express professionnel'
        };
        break;

      default:
        schemaData = baseBusinessSchema;
    }

    // Add FAQ schema if provided
    if (faqItems && faqItems.length > 0) {
      const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map(item => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer
          }
        }))
      };

      // If schemaData has @graph, add FAQ to it
      if (schemaData['@graph']) {
        schemaData['@graph'].push(faqSchema);
      } else {
        // Create a new @graph structure
        schemaData = {
          '@context': 'https://schema.org',
          '@graph': [schemaData, faqSchema]
        };
      }
    }

    // Create and append script
    const script = document.createElement('script');
    script.id = 'schema-org-jsonld';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById('schema-org-jsonld');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [type, faqItems, name, description, areaServed]);

  return null;
}